import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCartItems,
  addToCart,
  addToCartByVariants,
  incrementQuantity,
  decrementQuantity,
  removeCartItem,
  removeCart,
  applyCoupon,
  removeCoupon,
  fetchCartSummary
} from "../actions/cartAction";

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    items: [],
    cartItems: [],
    totalQuantity: 0,
    status: "idle",
    error: null,
    cartSummary: null,
  },
  reducers: {
    clearCartError: (state) => {
      state.error = null;
    },
    clearCart: (state) => {
      state.cartItems = { data: [] };
      state.totalQuantity = 0;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Add to cart
      .addCase(addToCart.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items.push(action.payload);

        // Update total quantity based on added item
        const addedQty = action.payload?.data?.qty || action.payload?.data?.quantity ||
          action.payload?.qty || action.payload?.quantity || 1;
        state.totalQuantity += Number(addedQty);

        state.error = null;
        // Dispatch event for non-redux components asynchronously
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        }, 0);
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to add item to cart";
      })

      // Add to cart by variants
      .addCase(addToCartByVariants.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addToCartByVariants.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items.push(action.payload);

        // Update total quantity if variant info is available
        // Usually variants addition is multiple, but we check common fields
        const addedQty = action.payload?.qty || action.payload?.quantity ||
          action.payload?.data?.qty || action.payload?.data?.quantity || 1;
        state.totalQuantity += Number(addedQty);

        state.error = null;
        // Dispatch event for non-redux components asynchronously
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        }, 0);
      })
      .addCase(addToCartByVariants.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to add item to cart";
      })

      // Fetch cart items
      .addCase(fetchCartItems.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCartItems.fulfilled, (state, action) => {
        state.status = "succeeded";

        // Robustly extract items array and normalize state.cartItems
        let items = [];
        const payload = action.payload;

        if (Array.isArray(payload)) {
          items = payload;
          state.cartItems = { data: items };
        } else if (payload?.data) {
          if (Array.isArray(payload.data)) {
            items = payload.data;
            state.cartItems = payload;
          } else if (Array.isArray(payload.data.items)) {
            items = payload.data.items;
            state.cartItems = { ...payload, data: items };
          } else if (Array.isArray(payload.items)) {
            items = payload.items;
            state.cartItems = { ...payload, data: items };
          } else {
            // If data is an object but doesn't have a known array, use it as is but items is []
            state.cartItems = payload;
          }
        } else if (Array.isArray(payload?.items)) {
          items = payload.items;
          state.cartItems = { data: items };
        } else {
          state.cartItems = { data: [] };
        }

        state.totalQuantity = items.reduce((total, item) =>
          total + (Number(item.qty) || Number(item.quantity) || 0), 0);

        state.error = null;
      })
      .addCase(fetchCartItems.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch cart items";
      })

      // Increment quantity
      .addCase(incrementQuantity.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(incrementQuantity.fulfilled, (state, action) => {
        const { cartItemId } = action.payload;
        const item = state.cartItems.data?.find((item) => item.id === cartItemId);
        if (item) {
          item.qty += 1;
          state.totalQuantity += 1;
        }
        state.status = "succeeded";
        state.error = null;
        // Dispatch event for non-redux components asynchronously
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        }, 0);
      })
      .addCase(incrementQuantity.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to increment quantity";
      })

      // Decrement quantity
      .addCase(decrementQuantity.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(decrementQuantity.fulfilled, (state, action) => {
        const { cartItemId } = action.payload;
        const item = state.cartItems.data?.find((item) => item.id === cartItemId);
        if (item && item.qty > 1) {
          item.qty -= 1;
          state.totalQuantity -= 1;
        }
        state.status = "succeeded";
        state.error = null;
        // Dispatch event for non-redux components asynchronously
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        }, 0);
      })
      .addCase(decrementQuantity.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to decrement quantity";
      })

      // Remove item
      .addCase(removeCartItem.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        const id = action.payload;
        const item = state.cartItems.data?.find((item) => item.id === id);
        if (item) {
          state.totalQuantity -= item.qty || 0;
          state.cartItems.data = state.cartItems.data.filter((item) => item.id !== id);
        }
        state.status = "succeeded";
        state.error = null;
        // Dispatch event for non-redux components asynchronously
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        }, 0);
      })
      .addCase(removeCartItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to remove item";
      })

      // Clear cart
      .addCase(removeCart.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(removeCart.fulfilled, (state) => {
        state.status = "succeeded";
        state.cartItems = { data: [] }; // Keep normalized structure
        state.totalQuantity = 0;
        state.error = null;
        // Dispatch event for non-redux components asynchronously
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('cartUpdated'));
        }, 0);
      })
      .addCase(removeCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to clear cart";
      })
      // Apply coupon
      .addCase(applyCoupon.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(applyCoupon.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.cartSummary = action.payload;
        state.error = null;
      })
      .addCase(applyCoupon.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to apply coupon";
      })
      // Remove coupon
      .addCase(removeCoupon.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(removeCoupon.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(removeCoupon.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to remove coupon";
      })
      // Fetch cart summary
      .addCase(fetchCartSummary.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCartSummary.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.cartSummary = action.payload;
        state.error = null;
      })
      .addCase(fetchCartSummary.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch cart summary";
      });
  },
});

export const { clearCartError, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
